import { TriangleAlert } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { IPurchaseRequestItemBody } from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';

interface ConfirmDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  itemsRequiringConfirmation: Array<{
    item: IPurchaseRequestItemBody;
    reasons: string[];
  }>;
  onConfirm: () => void;
  onCancel: () => void;
}

// Memoized item card component to prevent unnecessary re-renders
const WarningItem = memo(
  ({
    item,
    reasons,
  }: {
    item: IPurchaseRequestItemBody;
    reasons: string[];
  }) => (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/30">
      <div className="mb-2 flex-1 text-lg font-medium">{item.ItemName}</div>
      <div className="flex flex-col gap-1">
        {reasons.map((reason, rIndex) => (
          <div
            key={rIndex}
            className="rounded-md bg-red-100 px-2 py-1 text-sm font-medium text-red-800 dark:bg-red-900/50 dark:text-red-200"
          >
            {reason}
          </div>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
        <div>
          <span className="text-muted-foreground">الكمية:</span>{' '}
          <span className="font-medium">{item.Qty}</span>
        </div>
        <div>
          <span className="text-muted-foreground">السعر:</span>{' '}
          <span className="font-medium">{item.Price}</span>
        </div>
        <div>
          <span className="text-muted-foreground">الخصم:</span>{' '}
          <span className="font-medium">{item.Discount}%</span>
        </div>
        <div>
          <span className="text-muted-foreground">الإجمالي:</span>{' '}
          <span className="font-medium">{item.TotalAfterDiscount}</span>
        </div>
      </div>
    </div>
  ),
);
WarningItem.displayName = 'WarningItem';

// Memoized warning message component
const WarningMessage = memo(() => (
  <div className="rounded-md bg-red-50 p-3 text-sm dark:bg-red-950/20">
    <p className="mb-2 flex items-center text-red-700 dark:text-red-400">
      <span className="mr-2 text-lg">⚠️</span>
      بالرغم من المشكلات الموضحة، هل ترغب في المتابعة وحفظ الطلب؟
    </p>
    <p className="flex items-center font-medium text-red-700 dark:text-red-400">
      <span className="mr-2 text-lg">📝</span>
      وهذه الطلبية سوف تكون معلقة حتي الموافقة عليها من المسئول
    </p>
  </div>
));
WarningMessage.displayName = 'WarningMessage';

// Main component memoized
const ConfirmDialog = memo(
  ({
    isOpen,
    onOpenChange,
    itemsRequiringConfirmation,
    onConfirm,
    onCancel,
  }: ConfirmDialogProps) => {
    // Memoize event handlers to prevent recreating them on every render
    const handleConfirm = useCallback(() => {
      onConfirm();
    }, [onConfirm]);

    const handleCancel = useCallback(() => {
      onCancel();
    }, [onCancel]);

    // Memoize the items list for better performance with larger lists
    const itemsList = useMemo(
      () =>
        itemsRequiringConfirmation.map((item, index) => (
          <WarningItem
            key={`${item.item.Item_ID || ''}-${index}`}
            item={item.item}
            reasons={item.reasons}
          />
        )),
      [itemsRequiringConfirmation],
    );

    // Optimize rendering by checking if dialog should be shown
    if (!isOpen) return null;

    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent size="lg" hiddenCloseIcon>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <TriangleAlert className="h-5 w-5" />
              <span>تحذير - هل أنت متأكد من حفظ هذه الأصناف؟ 🤔</span>
            </DialogTitle>
            <DialogDescription className="text-right text-red-600/90">
              تم العثور على بعض الأصناف التي قد تحتاج إلى مراجعة قبل الحفظ!
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[400px] rounded-md border p-4">
            <div className="flex flex-col gap-3">{itemsList}</div>
          </ScrollArea>

          <WarningMessage />

          <DialogFooter className="flex gap-2 sm:justify-start">
            <Button variant="outline" onClick={handleCancel}>
              إلغاء
            </Button>
            <Button
              onClick={handleConfirm}
              className="bg-gradient-to-b from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 dark:from-red-700 dark:to-red-800 dark:hover:from-red-800 dark:hover:to-red-900"
            >
              نعم، حفظ على أي حال 😎
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);

ConfirmDialog.displayName = 'ConfirmDialog';

export default ConfirmDialog;
